// @services/clients/DocumentAssistantDictationApi.ts
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";

export type DictationJsonValue =
    | string
    | number
    | boolean
    | DictationJsonObject
    | readonly DictationJsonValue[];
export type DictationJsonObject = { readonly [key: string]: DictationJsonValue };
export type DictationPayloadValue =
    | string
    | number
    | boolean
    | Blob
    | DictationPayloadObject
    | readonly DictationPayloadValue[];
export type DictationPayloadObject = { readonly [key: string]: DictationPayloadValue };

export const DocumentAssistantDictationContract = {
    contractId: "document.assistant.dictation",
    transcriptionRoute: "/v1/transcription",
} as const;

declare const DictationMimeTypeBrand: unique symbol;
declare const DictationAudioExtensionBrand: unique symbol;
declare const DictationDurationMillisBrand: unique symbol;
declare const DictationTranscriptTextBrand: unique symbol;
declare const DictationTraceIdBrand: unique symbol;
declare const DictationErrorInfoTextBrand: unique symbol;

export type DictationFieldId = "prompt" | "documentDetailsPrompt" | "refine" | "finalEdits";

export type DictationRecordingState = "idle" | "recording" | "stopping" | "transcribing" | "failed";

export type DictationMimeType = string & {
    readonly [DictationMimeTypeBrand]: "DictationMimeType";
};

export type DictationAudioExtension = string & {
    readonly [DictationAudioExtensionBrand]: "DictationAudioExtension";
};

export type DictationDurationMillis = number & {
    readonly [DictationDurationMillisBrand]: "DictationDurationMillis";
};

export type DictationTranscriptText = string & {
    readonly [DictationTranscriptTextBrand]: "DictationTranscriptText";
};

export type DictationTraceId = string & {
    readonly [DictationTraceIdBrand]: "DictationTraceId";
};

export type DictationErrorInfoText = string & {
    readonly [DictationErrorInfoTextBrand]: "DictationErrorInfoText";
};

export type DictationAudioCapture = {
    readonly blob: Blob;
    readonly mimeType: DictationMimeType;
    readonly extension: DictationAudioExtension;
    readonly durationMillis: DictationDurationMillis;
    readonly fieldId: DictationFieldId;
};

export type DictationTranscriptionRequest = {
    readonly capture: DictationAudioCapture;
};

export type DictationTranscriptionResponse = {
    readonly transcript: DictationTranscriptText;
    readonly traceId: OptionTs<DictationTraceId>;
};

export type DictationErrorCode =
    | "permissionDenied"
    | "noMicrophone"
    | "unsupportedBrowser"
    | "emptyRecording"
    | "authRequired"
    | "uploadFailed"
    | "networkError"
    | "transcriptionUnavailable"
    | "responseDecodeError";

export type DictationError = {
    readonly code: DictationErrorCode;
    readonly message: DictationErrorInfoText;
    readonly fieldId: OptionTs<DictationFieldId>;
    readonly traceId: OptionTs<DictationTraceId>;
};

export type DictationApiDecodeResult<T> =
    | {
          readonly type: "ok";
          readonly value: T;
      }
    | {
          readonly type: "error";
          readonly message: DictationErrorInfoText;
      };

const brandString = <Brand extends string>(value: string): Brand => value as Brand;
const brandNumber = <Brand extends number>(value: number): Brand => value as Brand;

const decodeOk = <T>(value: T): DictationApiDecodeResult<T> => ({ type: "ok", value });
const decodeError = (message: string): DictationApiDecodeResult<never> => ({
    type: "error",
    message: makeDictationErrorInfoText(message),
});

const isRecord = (value: DictationPayloadValue): value is DictationPayloadObject =>
    !(value instanceof Blob) && typeof value === "object" && !Array.isArray(value);

const trimNonEmpty = (value: string, fallback: string): string => {
    const trimmed = value.trim();
    return trimmed === "" ? fallback : trimmed;
};

const decodeString = (value: DictationPayloadValue, description: string): DictationApiDecodeResult<string> => {
    if (typeof value !== "string") {
        return decodeError(`${description} must be a string.`);
    }
    return decodeOk(value);
};

const decodeFieldId = (value: DictationPayloadValue): DictationApiDecodeResult<DictationFieldId> => {
    if (value === "prompt" || value === "documentDetailsPrompt" || value === "refine" || value === "finalEdits") {
        return decodeOk(value);
    }
    return decodeError("Dictation field id must identify a supported document assistant field.");
};

const decodeErrorCode = (value: DictationPayloadValue): DictationApiDecodeResult<DictationErrorCode> => {
    switch (value) {
        case "permissionDenied":
        case "noMicrophone":
        case "unsupportedBrowser":
        case "emptyRecording":
        case "authRequired":
        case "uploadFailed":
        case "networkError":
        case "transcriptionUnavailable":
        case "responseDecodeError":
            return decodeOk(value);
        default:
            return decodeError("Dictation error code must be a supported failure mode.");
    }
};

export const makeDictationMimeType = (value: string): DictationMimeType =>
    brandString<DictationMimeType>(trimNonEmpty(value, "audio/webm"));

export const makeDictationAudioExtension = (value: string): DictationAudioExtension => {
    const trimmed = value.trim().replace(/^\./u, "");
    return brandString<DictationAudioExtension>(trimmed === "" ? "webm" : trimmed);
};

export const makeDictationDurationMillis = (value: number): DictationDurationMillis =>
    brandNumber<DictationDurationMillis>(Math.max(0, Math.trunc(Number.isFinite(value) ? value : 0)));

export const makeDictationTranscriptText = (value: string): DictationApiDecodeResult<DictationTranscriptText> => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return decodeError("Dictation transcript must be non-empty.");
    }
    return decodeOk(brandString<DictationTranscriptText>(trimmed));
};

export const makeDictationTraceId = (value: string): DictationApiDecodeResult<DictationTraceId> => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return decodeError("Dictation trace id must be non-empty when present.");
    }
    return decodeOk(brandString<DictationTraceId>(trimmed));
};

export const makeDictationErrorInfoText = (value: string): DictationErrorInfoText =>
    brandString<DictationErrorInfoText>(trimNonEmpty(value, "Dictation request failed."));

export const dictationError = (
    code: DictationErrorCode,
    message: string,
    fieldId: OptionTs<DictationFieldId>,
    traceId: OptionTs<DictationTraceId>,
): DictationError => ({
    code,
    message: makeDictationErrorInfoText(message),
    fieldId,
    traceId,
});

export const decodeDictationAudioCapture = (payload: DictationPayloadValue): DictationApiDecodeResult<DictationAudioCapture> => {
    if (!isRecord(payload)) {
        return decodeError("Dictation audio capture must be an object.");
    }

    if (!(payload.blob instanceof Blob)) {
        return decodeError("Dictation audio capture blob must be a Blob.");
    }

    if (payload.blob.size <= 0) {
        return decodeError("Dictation audio capture blob must not be empty.");
    }

    const mimeType = decodeString(payload.mimeType, "Dictation audio MIME type");
    if (mimeType.type === "error") {
        return mimeType;
    }

    const extension = decodeString(payload.extension, "Dictation audio extension");
    if (extension.type === "error") {
        return extension;
    }

    if (typeof payload.durationMillis !== "number" || !Number.isFinite(payload.durationMillis) || payload.durationMillis < 0) {
        return decodeError("Dictation duration must be a non-negative finite number.");
    }

    const fieldId = decodeFieldId(payload.fieldId);
    if (fieldId.type === "error") {
        return fieldId;
    }

    return decodeOk({
        blob: payload.blob,
        mimeType: makeDictationMimeType(mimeType.value),
        extension: makeDictationAudioExtension(extension.value),
        durationMillis: makeDictationDurationMillis(payload.durationMillis),
        fieldId: fieldId.value,
    });
};

export const decodeDictationTranscriptionRequest = (
    payload: DictationPayloadValue,
): DictationApiDecodeResult<DictationTranscriptionRequest> => {
    if (!isRecord(payload)) {
        return decodeError("Dictation transcription request must be an object.");
    }

    const capture = decodeDictationAudioCapture(payload.capture);
    if (capture.type === "error") {
        return capture;
    }

    return decodeOk({ capture: capture.value });
};

export const decodeDictationTranscriptionResponse = (
    payload: DictationPayloadValue,
): DictationApiDecodeResult<DictationTranscriptionResponse> => {
    if (!isRecord(payload)) {
        return decodeError("Dictation transcription response must be an object.");
    }

    if (typeof payload.transcript !== "string") {
        return decodeError("Dictation transcription response transcript must be a string.");
    }

    const transcript = makeDictationTranscriptText(payload.transcript);
    if (transcript.type === "error") {
        return transcript;
    }

    if (typeof payload.traceId === "string") {
        const traceId = makeDictationTraceId(payload.traceId);
        if (traceId.type === "error") {
            return traceId;
        }
        return decodeOk({
            transcript: transcript.value,
            traceId: Option.some(traceId.value),
        });
    }

    return decodeOk({
        transcript: transcript.value,
        traceId: Option.none<DictationTraceId>(),
    });
};

export const decodeDictationTranscriptionRefusal = (payload: DictationPayloadValue): DictationApiDecodeResult<DictationError> => {
    if (!isRecord(payload)) {
        return decodeError("Dictation transcription refusal must be an object.");
    }

    const code = decodeErrorCode(payload.code);
    if (code.type === "error") {
        return code;
    }

    const message = decodeString(payload.message, "Dictation refusal message");
    if (message.type === "error") {
        return message;
    }

    const fieldIdOption = (() => {
        if (typeof payload.fieldId !== "string") {
            return decodeOk(Option.none<DictationFieldId>());
        }
        const fieldId = decodeFieldId(payload.fieldId);
        if (fieldId.type === "error") {
            return fieldId;
        }
        return decodeOk(Option.some(fieldId.value));
    })();
    if (fieldIdOption.type === "error") {
        return fieldIdOption;
    }

    if (typeof payload.traceId === "string") {
        const traceId = makeDictationTraceId(payload.traceId);
        if (traceId.type === "error") {
            return traceId;
        }
        return decodeOk(dictationError(code.value, message.value, fieldIdOption.value, Option.some(traceId.value)));
    }

    return decodeOk(dictationError(code.value, message.value, fieldIdOption.value, Option.none()));
};
